import * as Sentry from "@sentry/browser";
import { MESSAGE_AUTH_CODES } from "constant/messages";
import { captureStack } from "integrations/sentry";
import get from "lodash/get";
import { all, call, put, takeLatest } from "redux-saga/effects";
import {
  createApiAction,
  createApiSaga,
  directServiceSaga,
} from "redux-store/api-sagas";
import { addErrorMessageAction } from "redux-store/messages";
import { s3Service } from "service/s3";
import { userContentService } from "service/user-content";

import {
  LOAD_USER_CONTENTS,
  LOAD_USER_CONTENTS_FAILURE,
  EDIT_USER_CONTENT,
  EDIT_USER_CONTENT_ERROR,
  EDIT_USER_CONTENT_SUCCESS,
  UPLOAD_USER_CONTENT,
  UPLOAD_USER_CONTENT_ERROR,
  UPLOAD_USER_CONTENT_SUCCESS,
  UPLOAD_USER_CONTENT_S3,
  uploadUserContentAction,
} from "./actions";

const reloadUserContentsSaga = createApiSaga({
  service: userContentService.load,
  serviceName: "userContentService.load",
  successAction: LOAD_USER_CONTENTS,
  failureAction: LOAD_USER_CONTENTS_FAILURE,
  reload: true,
});

const editUserContentSaga = createApiSaga({
  service: userContentService.edit,
  serviceName: "userContentService.edit",
  fetchAction: EDIT_USER_CONTENT,
  successAction: EDIT_USER_CONTENT_SUCCESS,
  failureAction: EDIT_USER_CONTENT_ERROR,
});

const uploadUserContentSaga = createApiSaga({
  service: userContentService.upload,
  serviceName: "userContentService.upload",
  fetchAction: UPLOAD_USER_CONTENT,
  successAction: UPLOAD_USER_CONTENT_SUCCESS,
  failureAction: UPLOAD_USER_CONTENT_ERROR,
});

function* fetchS3AuthSaga(config) {
  const { filename } = config;
  const authSagaConfig = {
    failureAction: UPLOAD_USER_CONTENT_ERROR,
    service: s3Service.auth,
    serviceName: "S3Service.auth",
    serviceArgs: [filename],
  };

  const response = yield call(directServiceSaga, authSagaConfig);
  return get(response, "data", {});
}

function* uploadToS3Saga(authData, file) {
  const { presignedUrl } = authData;
  try {
    yield call(s3Service.upload, presignedUrl, { file, ...authData });
  } catch (response) {
    // Messaging ignores certain error codes, so we convert to 400 in case
    // AWS returns this status code.
    let { status } = response;
    if (MESSAGE_AUTH_CODES.includes(status)) {
      status = 400;
    }

    // AWS returns errors as an XML response. To avoid custom work parsing
    // this response, we display a custom error message.
    const message = "Failed to upload file to s3.";
    yield all([
      put(addErrorMessageAction(message, "S3Service.upload", status)),
      put(
        createApiAction(UPLOAD_USER_CONTENT_ERROR, response, status, message),
      ),
    ]);

    captureStack();
    Sentry.captureEvent({
      message,
      extra: response,
      level: Sentry.Severity.Warning,
    });

    throw response;
  }
}

function* uploadAndCreateContentCaller(action) {
  const { payload = {}, successMessage } = action;
  const { serviceArgs = [] } = payload;
  const [file, filename, name, duration, folderId] = serviceArgs;

  let authData;
  try {
    authData = yield call(fetchS3AuthSaga, { filename });
  } catch (e) {
    return;
  }

  try {
    yield call(uploadToS3Saga, authData, file);
  } catch (e) {
    return;
  }

  const createPayload = {
    mimeType: authData["Content-Type"],
    s3Url: authData.cdn_url,
    duration,
    folderId,
    name,
  };
  yield put(uploadUserContentAction(createPayload, successMessage));
}

function* uploadUserContentToS3Saga() {
  yield takeLatest(UPLOAD_USER_CONTENT_S3, uploadAndCreateContentCaller);
}

const userContentSagas = [
  editUserContentSaga(),
  reloadUserContentsSaga(),
  uploadUserContentSaga(),
  uploadUserContentToS3Saga(),
];

export {
  fetchS3AuthSaga,
  uploadAndCreateContentCaller,
  uploadToS3Saga,
  userContentSagas,
};
